import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash, FaTimes, FaBars, FaChevronLeft, FaChevronDown, FaChevronUp, FaSync, FaEdit, FaCopy, FaLayerGroup, FaSearch, FaArchive, FaBoxOpen } from 'react-icons/fa';
import { FiTrash, FiFileText } from 'react-icons/fi';
import InputClientes from '../util/Inputs/InputClientes';
import InputProveedor from '../util/Inputs/InputProveedor';
import PlantillaModal from '../util/PlantillaModal';
import TablaEntidad from '../util/tablas/TablaEntidad';
import SpinnerCargando from '../util/SpinnerCargando';
import FormularioDetalleGasto from './FormularioDetalleGasto';
import ModalBulkAddLineasMejorado from './ModalBulkAddLineasMejorado';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import DockVerticalDerecha from '../estructura/DockVerticalDerecha';
import {
  CONCEPTOS,
  ITBIS_OPTIONS,
  ITBIS_RETENIDO_OPTIONS,
  TIPOS_NCF,
  MESES,
  TIPOS_FACTURA,
  FORMAS_PAGO,
  TIPOS_RETENCION,
  calcularITBIS,
  calcularITBISRetenido,
  calcularISRPorTipo,
  calcularTotalFacturado,
  convertirMesANumero,
  obtenerDatosDeConcepto,
  validarFormatoNCF,
  detectarTipoNCF,
  crearLineaVacia,
  exportarCSV,
  exportarTXT606,
  validarLinea,
  validarTodas,
  validarNCFDuplicados,
  calcularTotales
} from './Funciones/FuncionesProcesoCostosGastos';

/**
 * Componente principal para gestión de Costos y Gastos
 * Optimizado para productividad máxima con atajos de teclado y auto-cálculos
 */
export default function ProcesoCostosGastos() {
  const currentYear = new Date().getFullYear();
  const currentMonth = MESES[new Date().getMonth()].value;

  // Estado principal del formulario
  const [formData, setFormData] = useState({
    año: currentYear,
    periodo: currentMonth,
    id_entidad_cliente: null,
    nombre_cliente: '',
    rnc_cliente: '',
    destino_itbis: '', // '' = no seleccionado, 'proporcionalidad' o 'costo'
    detalles: []
  });

  // Estados para modales
  const [mostrarModalCliente, setMostrarModalCliente] = useState(false);
  const [contextoModalCliente, setContextoModalCliente] = useState('formulario'); // 'formulario' o 'filtro'
  const [mostrarModalProveedor, setMostrarModalProveedor] = useState(false);
  const [lineaEditandoProveedor, setLineaEditandoProveedor] = useState(null);
  const [mostrarModalConfirmacionLimpiar, setMostrarModalConfirmacionLimpiar] = useState(false);
  const [mostrarModalBulkAdd, setMostrarModalBulkAdd] = useState(false);

  // Referencias para inputs de proveedores (uno por línea)
  const inputProveedorRefs = useRef({});

  // Estados de UI
  const [cargando, setCargando] = useState(false);
  const [sidebarOculto, setSidebarOculto] = useState(false);

  // Estados del formulario de captura/edición
  const [formularioDetalle, setFormularioDetalle] = useState(crearLineaVacia(currentMonth));
  const [modoFormulario, setModoFormulario] = useState('nuevo'); // 'nuevo' | 'editando'
  const [lineaEditandoIndex, setLineaEditandoIndex] = useState(null);
  const [celdaEditable, setCeldaEditable] = useState(null); // {index, campo} para edición inline

  // Estados para guardar/cargar reportes
  const [idReporteActual, setIdReporteActual] = useState(null); // null = nuevo, número = editando
  const [modoEdicionReporte, setModoEdicionReporte] = useState(false);
  const [mostrarModalCargarReporte, setMostrarModalCargarReporte] = useState(false);
  const [reportesDisponibles, setReportesDisponibles] = useState([]);
  const [filtrosReporte, setFiltrosReporte] = useState({
    año: currentYear,
    periodo: '',
    id_entidad_cliente: null,
    nombre_cliente_filtro: '',
    mostrarArchivados: false
  });
  const [reporteArchivado, setReporteArchivado] = useState(false);

  // Estados para autoguardado
  const [estadoAutoguardado, setEstadoAutoguardado] = useState('inactivo');
  // 'inactivo' | 'guardando' | 'guardado' | 'error' | 'sin_cambios'
  const [ultimoGuardado, setUltimoGuardado] = useState(null); // timestamp
  const [infoAutoguardado, setInfoAutoguardado] = useState({ lineasGuardadas: 0, lineasTotales: 0 });
  const [segundosRestantes, setSegundosRestantes] = useState(120); // 2 minutos

  // Estados para filtros de líneas
  const [filtrosLineas, setFiltrosLineas] = useState({
    ncf: '',
    rnc: ''
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  // Referencias para autoguardado
  const formDataAnteriorRef = useRef(null);
  const intervaloAutoguardadoRef = useRef(null);
  const ejecutarAutoguardadoRef = useRef(null); // Ref estable para la función

  // Calcular totales
  const totales = calcularTotales(formData.detalles);

  // Filtrar líneas según filtros activos
  const lineasFiltradas = useMemo(() => {
    // Función para limpiar RNC (remover guiones, espacios y caracteres especiales)
    const limpiarRNC = (rnc) => {
      if (!rnc) return '';
      return rnc.toString().replace(/[-\s]/g, '').toLowerCase();
    };

    return formData.detalles.map((linea, index) => ({ ...linea, _originalIndex: index })).filter(linea => {
      // Filtro por NCF (búsqueda parcial, insensible a mayúsculas)
      if (filtrosLineas.ncf && !linea.ncf?.toLowerCase().includes(filtrosLineas.ncf.toLowerCase())) {
        return false;
      }
      // Filtro por RNC del proveedor (limpia guiones y espacios para comparación)
      if (filtrosLineas.rnc) {
        const rncBusqueda = limpiarRNC(filtrosLineas.rnc);
        const rncLinea = limpiarRNC(linea.rnc_proveedor);
        if (!rncLinea.includes(rncBusqueda)) {
          return false;
        }
      }
      return true;
    });
  }, [formData.detalles, filtrosLineas]);

  // Helper para verificar si hay filtros activos
  const hayFiltrosActivos = filtrosLineas.ncf || filtrosLineas.rnc;

  // Limpiar todos los filtros
  const limpiarFiltrosLineas = () => {
    setFiltrosLineas({ ncf: '', rnc: '' });
  };

  // Efecto para ocultar/mostrar sidebar
  useEffect(() => {
    const sidebar = document.querySelector('aside');
    if (sidebar) {
      if (sidebarOculto) {
        sidebar.style.display = 'none';
      } else {
        sidebar.style.display = '';
      }
    }

    // Cleanup: restaurar sidebar al desmontar componente
    return () => {
      const sidebar = document.querySelector('aside');
      if (sidebar) {
        sidebar.style.display = '';
      }
    };
  }, [sidebarOculto]);

  // ==================== Handlers del Header ====================

  const handleClienteSeleccionado = (cliente) => {
    if (contextoModalCliente === 'filtro') {
      // Actualizar filtros y buscar
      handleClienteSeleccionadoFiltro(cliente);
    } else {
      // Actualizar formulario principal
      setFormData(prev => ({
        ...prev,
        id_entidad_cliente: cliente.id_entidad,
        nombre_cliente: `${cliente.nombre} ${cliente.apellido || ''}`.trim(),
        rnc_cliente: cliente.rnc || cliente.cedula || ''
      }));
      setMostrarModalCliente(false);
      toast.success(
        <div>
          <strong>✓ Cliente seleccionado</strong>
          <div className="text-sm mt-1">{cliente.nombre || 'Cliente agregado al formulario'}</div>
        </div>,
        { autoClose: 2000 }
      );
    }
  };

  const handleCambioHeader = (campo, valor) => {
    setFormData(prev => {
      const newData = { ...prev, [campo]: valor };
      
      // Si cambia el periodo, actualizar todas las líneas existentes con el número del mes
      if (campo === 'periodo') {
        newData.detalles = prev.detalles.map(det => ({
          ...det,
          mes: convertirMesANumero(valor)
        }));
        
        // También actualizar el formulario detalle (línea en edición/nueva)
        setFormularioDetalle(prevForm => ({
          ...prevForm,
          mes: convertirMesANumero(valor)
        }));
      }
      
      return newData;
    });
  };

  const handleCambioDestinoITBIS = (nuevoDestino) => {
    setFormData(prev => {
      const nuevosDetalles = prev.detalles.map(detalle => {
        const tipoFactura = detalle.tipo_factura;
        const itbisFacturado = detalle.itbis_facturado || 0;

        if (nuevoDestino === 'costo') {
          return { ...detalle, itbis_proporcionalidad: 0, itbis_costo: itbisFacturado };
        } else if (nuevoDestino === 'proporcionalidad') {
          if (tipoFactura === 'Servicios') {
            return { ...detalle, itbis_proporcionalidad: itbisFacturado, itbis_costo: 0 };
          }
          return { ...detalle, itbis_proporcionalidad: 0, itbis_costo: 0 };
        } else {
          return { ...detalle, itbis_proporcionalidad: 0, itbis_costo: 0 };
        }
      });
      
      return {
        ...prev,
        destino_itbis: nuevoDestino,
        detalles: nuevosDetalles
      };
    });

    setFormularioDetalle(prev => {
      const tipoFactura = prev.tipo_factura;
      const itbisFacturado = prev.itbis_facturado || 0;

      if (nuevoDestino === 'costo') {
        return { ...prev, itbis_proporcionalidad: 0, itbis_costo: itbisFacturado };
      } else if (nuevoDestino === 'proporcionalidad') {
        if (tipoFactura === 'Servicios') {
          return { ...prev, itbis_proporcionalidad: itbisFacturado, itbis_costo: 0 };
        }
        return { ...prev, itbis_proporcionalidad: 0, itbis_costo: 0 };
      } else {
        return { ...prev, itbis_proporcionalidad: 0, itbis_costo: 0 };
      }
    });

    if (nuevoDestino) {
      const textos = {
        proporcionalidad: 'ITBIS sujeto a Proporcionalidad (solo Servicios)',
        costo: 'ITBIS llevado al Costo (Bienes y Servicios)',
        adelantar: 'ITBIS por Adelantar'
      };
      toast.success(
        <div>
          <strong>✓ Destino ITBIS actualizado</strong>
          <div className="text-sm mt-1">
            {textos[nuevoDestino] || nuevoDestino}
          </div>
        </div>,
        { autoClose: 3000 }
      );
    }
  };

  // ==================== Handler Bulk Add ====================

  const handleLineasBulkAgregadas = (nuevasLineas) => {
    setFormData(prev => ({
      ...prev,
      detalles: [...prev.detalles, ...nuevasLineas]
    }));
  };

  // ==================== Handlers de Líneas de Detalle ====================

  // Handler para cambios en el formulario de detalle
  const handleCambioFormularioDetalle = (campo, valor) => {
    setFormularioDetalle(prev => {
      const nuevoDetalle = {
        ...prev,
        [campo]: valor
      };

      // Auto-detectar tipo NCF cuando cambia el NCF
      if (campo === 'ncf') {
        const tipoDetectado = detectarTipoNCF(valor);
        if (tipoDetectado) {
          nuevoDetalle.tipo_ncf = tipoDetectado;
        }
      }

      // Auto-cálculo de Monto Facturado cuando cambia monto o descuento
      if (campo === 'monto' || campo === 'descuento') {
        const monto = campo === 'monto' ? valor : prev.monto;
        const descuento = campo === 'descuento' ? valor : prev.descuento;
        nuevoDetalle.monto_facturado = parseFloat(
          ((parseFloat(monto) || 0) - (parseFloat(descuento) || 0)).toFixed(2)
        );
      }

      // Auto-cálculo de ITBIS Facturado cuando cambia el porcentaje de ITBIS, monto o descuento
      if (campo === 'itbis_porcentaje' || campo === 'monto' || campo === 'descuento') {
        const monto = campo === 'monto' ? valor : prev.monto;
        const descuento = campo === 'descuento' ? valor : prev.descuento;
        const porcentajeITBIS = campo === 'itbis_porcentaje' ? valor : prev.itbis_porcentaje;
        
        // Solo calcular si hay un porcentaje seleccionado (no exento ni vacío)
        if (porcentajeITBIS && porcentajeITBIS !== 'exento' && porcentajeITBIS !== 0) {
          const montoBase = (parseFloat(monto) || 0) - (parseFloat(descuento) || 0);
          nuevoDetalle.itbis_facturado = parseFloat(
            (montoBase * (porcentajeITBIS / 100)).toFixed(2)
          );
        } else if (porcentajeITBIS === 'exento' || porcentajeITBIS === 0) {
          // Si es exento o 0%, poner ITBIS en 0
          nuevoDetalle.itbis_facturado = 0;
        }
        // Si no hay porcentaje seleccionado, no tocar el itbis_facturado (manual)
      }

      // Auto-cálculo de ITBIS retenido cuando cambia el porcentaje de retención o ITBIS facturado
      if (campo === 'itbis_retenido_porcentaje' || campo === 'itbis_facturado') {
        const itbisFacturado = campo === 'itbis_facturado' ? valor : prev.itbis_facturado;
        const porcentajeRetencion = campo === 'itbis_retenido_porcentaje' ? valor : prev.itbis_retenido_porcentaje;
        nuevoDetalle.itbis_retenido = calcularITBISRetenido(
          parseFloat(itbisFacturado) || 0,
          porcentajeRetencion
        );
      }

      // Auto-cálculo de ISR retenido cuando cambia monto, descuento, tipo_retencion o tipo de factura
      if (campo === 'monto' || campo === 'descuento' || campo === 'tipo_retencion' || campo === 'tipo_factura') {
        const monto = campo === 'monto' ? valor : prev.monto;
        const descuento = campo === 'descuento' ? valor : prev.descuento;
        const tipoRetencion = campo === 'tipo_retencion' ? valor : prev.tipo_retencion;
        const tipoFactura = campo === 'tipo_factura' ? valor : prev.tipo_factura;
        
        nuevoDetalle.isr_retenido = calcularISRPorTipo(
          parseFloat(monto) || 0,
          parseFloat(descuento) || 0,
          tipoRetencion,
          tipoFactura
        );
        
        // Si cambia a Bienes, resetear ISR a 0
        if (tipoFactura === 'Bienes') {
          nuevoDetalle.tipo_retencion = '';
          nuevoDetalle.isr_retenido = 0;
        }
      }

      // Auto-rellenar campos cuando cambia Concepto
      if (campo === 'concepto') {
        const datos = obtenerDatosDeConcepto(valor);
        if (datos) {
          nuevoDetalle.tipo_gasto_606 = datos.tipo_gasto_606;
          nuevoDetalle.tipo_factura = datos.tipo_factura;
          nuevoDetalle.itbis_retenido_porcentaje = datos.itbis_retenido_porcentaje;
          nuevoDetalle.tipo_retencion = datos.tipo_retencion;
          
          if (datos.tipo_factura === 'Bienes') {
            nuevoDetalle.tipo_retencion = '';
            nuevoDetalle.isr_retenido = 0;
          }
          
          // Recalcular ITBIS retenido si hay ITBIS facturado
          if (prev.itbis_facturado > 0) {
            nuevoDetalle.itbis_retenido = calcularITBISRetenido(
              prev.itbis_facturado,
              datos.itbis_retenido_porcentaje
            );
          }
        }
      }

      if (campo === 'itbis_facturado' || campo === 'tipo_factura') {
        const tipoFactura = nuevoDetalle.tipo_factura;
        const itbisFacturado = nuevoDetalle.itbis_facturado;

        if (formData.destino_itbis === 'costo') {
          nuevoDetalle.itbis_proporcionalidad = 0;
          nuevoDetalle.itbis_costo = itbisFacturado;
        } else if (formData.destino_itbis === 'proporcionalidad') {
          if (tipoFactura === 'Servicios') {
            nuevoDetalle.itbis_proporcionalidad = itbisFacturado;
            nuevoDetalle.itbis_costo = 0;
          } else {
            nuevoDetalle.itbis_proporcionalidad = 0;
            nuevoDetalle.itbis_costo = 0;
          }
        } else {
          nuevoDetalle.itbis_proporcionalidad = 0;
          nuevoDetalle.itbis_costo = 0;
        }
      }

      // Auto-calcular Total Facturado cuando cambian sus componentes
      if (campo === 'monto' || campo === 'descuento' || campo === 'itbis_facturado' || 
          campo === 'impuesto_selectivo' || campo === 'otros_impuestos' || 
          campo === 'propina_legal' || campo === 'otros_cargos') {
        nuevoDetalle.total_facturado = calcularTotalFacturado(nuevoDetalle);
      }

      return nuevoDetalle;
    });
  };

  // Recalcular todos los valores automáticos del formulario
  const recalcularFormularioDetalle = () => {
    setFormularioDetalle(prev => {
      const datos = obtenerDatosDeConcepto(prev.concepto);
      const nuevoDetalle = {...prev};
      
      if (datos) {
        nuevoDetalle.tipo_gasto_606 = datos.tipo_gasto_606;
        nuevoDetalle.tipo_factura = datos.tipo_factura;
        nuevoDetalle.itbis_retenido_porcentaje = datos.itbis_retenido_porcentaje;
        nuevoDetalle.tipo_retencion = datos.tipo_retencion;
      }
      
      // Recalcular Monto Facturado
      nuevoDetalle.monto_facturado = parseFloat(
        ((parseFloat(prev.monto) || 0) - (parseFloat(prev.descuento) || 0)).toFixed(2)
      );
      
      // Recalcular ITBIS Facturado si hay porcentaje seleccionado
      if (prev.itbis_porcentaje && prev.itbis_porcentaje !== 'exento' && prev.itbis_porcentaje !== 0) {
        const montoBase = (parseFloat(prev.monto) || 0) - (parseFloat(prev.descuento) || 0);
        nuevoDetalle.itbis_facturado = parseFloat(
          (montoBase * (prev.itbis_porcentaje / 100)).toFixed(2)
        );
      } else if (prev.itbis_porcentaje === 'exento' || prev.itbis_porcentaje === 0) {
        nuevoDetalle.itbis_facturado = 0;
      }
      // Si no hay porcentaje, mantener el valor manual existente
      
      // Recalcular ITBIS Retenido
      nuevoDetalle.itbis_retenido = calcularITBISRetenido(
        nuevoDetalle.itbis_facturado,
        prev.itbis_retenido_porcentaje
      );
      
      // Recalcular ISR
      nuevoDetalle.isr_retenido = calcularISRPorTipo(
        prev.monto,
        prev.descuento,
        prev.tipo_retencion,
        prev.tipo_factura
      );
      
      const itbisParaDistribuir = nuevoDetalle.itbis_facturado;
      if (formData.destino_itbis === 'costo') {
        nuevoDetalle.itbis_proporcionalidad = 0;
        nuevoDetalle.itbis_costo = itbisParaDistribuir;
      } else if (formData.destino_itbis === 'proporcionalidad') {
        if (prev.tipo_factura === 'Servicios') {
          nuevoDetalle.itbis_proporcionalidad = itbisParaDistribuir;
          nuevoDetalle.itbis_costo = 0;
        } else {
          nuevoDetalle.itbis_proporcionalidad = 0;
          nuevoDetalle.itbis_costo = 0;
        }
      } else {
        nuevoDetalle.itbis_proporcionalidad = 0;
        nuevoDetalle.itbis_costo = 0;
      }
      
      // Recalcular Total Facturado
      nuevoDetalle.total_facturado = calcularTotalFacturado(nuevoDetalle);
      
      return nuevoDetalle;
    });
    
    toast.success(
      <div>
        <strong>✓ Valores recalculados</strong>
        <div className="text-sm mt-1">Todos los valores automáticos han sido actualizados</div>
      </div>,
      { autoClose: 2000 }
    );
  };

  // Limpiar formulario y volver a modo nuevo
  const limpiarFormulario = () => {
    setFormularioDetalle(crearLineaVacia(formData.periodo));
    setModoFormulario('nuevo');
    setLineaEditandoIndex(null);
  };

  // Guardar línea desde formulario (nueva o editada)
  const guardarDesdeFormulario = () => {
    // Validar formularioDetalle
    const validacion = validarLinea(formularioDetalle);
    if (!validacion.isValid) {
      toast.error(
        <div>
          <strong>✗ Errores en el formulario</strong>
          <div className="text-sm mt-2 space-y-1">
            {validacion.errors.map((error, idx) => (
              <div key={idx} className="text-red-600">• {error}</div>
            ))}
          </div>
        </div>,
        { autoClose: 6000 }
      );
      return;
    }
    
    if (modoFormulario === 'nuevo') {
      // Agregar nueva línea
      setFormData(prev => ({
        ...prev,
        detalles: [...prev.detalles, formularioDetalle]
      }));
      toast.success(
        <div>
          <strong>✓ Línea agregada</strong>
          <div className="text-sm mt-1">Nueva línea agregada exitosamente</div>
        </div>,
        { autoClose: 2000 }
      );
    } else {
      // Actualizar línea existente
      setFormData(prev => ({
        ...prev,
        detalles: prev.detalles.map((d, i) => 
          i === lineaEditandoIndex ? formularioDetalle : d
        )
      }));
      toast.success(
        <div>
          <strong>✓ Línea actualizada</strong>
          <div className="text-sm mt-1">Línea #{lineaEditandoIndex + 1} actualizada exitosamente</div>
        </div>,
        { autoClose: 2000 }
      );
    }
    
    // Limpiar formulario
    limpiarFormulario();
  };

  // Cargar línea en formulario para edición
  const editarLineaEnFormulario = (index) => {
    limpiarFiltrosLineas();
    setFormularioDetalle({...formData.detalles[index]});
    setModoFormulario('editando');
    setLineaEditandoIndex(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info(
      <div>
        <strong>✏️ Modo edición</strong>
        <div className="text-sm mt-1">Editando línea #{index + 1}</div>
      </div>,
      { autoClose: 2000 }
    );
  };

  const duplicarLinea = (index) => {
    limpiarFiltrosLineas();
    const lineaDuplicada = {...formData.detalles[index]};
    // Limpiar campos únicos
    lineaDuplicada.ncf = '';
    lineaDuplicada.ncf_modificado = '';
    lineaDuplicada.tipo_ncf = '';
    lineaDuplicada.dia = '';
    
    setFormularioDetalle(lineaDuplicada);
    setModoFormulario('nuevo');
    setLineaEditandoIndex(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info(
      <div>
        <strong>📋 Línea duplicada</strong>
        <div className="text-sm mt-1">Complete los campos únicos (Día, NCF)</div>
      </div>,
      { autoClose: 3000 }
    );
  };

  const agregarLinea = () => {
    const nuevaLinea = crearLineaVacia(formData.periodo);
    setFormData(prev => ({
      ...prev,
      detalles: [...prev.detalles, nuevaLinea]
    }));
    
    // Auto-focus en el primer campo de la nueva línea después de un pequeño delay
    setTimeout(() => {
      const inputs = document.querySelectorAll(`[data-row="${formData.detalles.length}"]`);
      if (inputs.length > 0) {
        inputs[0].focus();
      }
    }, 100);
  };

  const eliminarLinea = (index) => {
    limpiarFiltrosLineas();
    setFormData(prev => ({
      ...prev,
      detalles: prev.detalles.filter((_, i) => i !== index)
    }));
    toast.info(
      <div>
        <strong>ℹ Línea eliminada</strong>
        <div className="text-sm mt-1">La línea ha sido removida del formulario</div>
      </div>,
      { autoClose: 2000 }
    );
  };

  const recalcularLinea = (index) => {
    setFormData(prev => {
      const nuevosDetalles = [...prev.detalles];
      const detalle = nuevosDetalles[index];
      
      // Obtener datos del concepto
      const datos = obtenerDatosDeConcepto(detalle.concepto);
      if (datos) {
        // Recalcular todos los valores automáticos
        detalle.tipo_gasto_606 = datos.tipo_gasto_606;
        detalle.tipo_factura = datos.tipo_factura;
        detalle.itbis_retenido_porcentaje = datos.itbis_retenido_porcentaje;
        detalle.tipo_retencion = datos.tipo_retencion;
      }
      
      // Recalcular Monto Facturado
      detalle.monto_facturado = parseFloat(
        ((parseFloat(detalle.monto) || 0) - (parseFloat(detalle.descuento) || 0)).toFixed(2)
      );
      
      // Recalcular ITBIS Facturado si hay porcentaje seleccionado
      if (detalle.itbis_porcentaje && detalle.itbis_porcentaje !== 'exento' && detalle.itbis_porcentaje !== 0) {
        const montoBase = (parseFloat(detalle.monto) || 0) - (parseFloat(detalle.descuento) || 0);
        detalle.itbis_facturado = parseFloat(
          (montoBase * (detalle.itbis_porcentaje / 100)).toFixed(2)
        );
      } else if (detalle.itbis_porcentaje === 'exento' || detalle.itbis_porcentaje === 0) {
        detalle.itbis_facturado = 0;
      }
      // Si no hay porcentaje, mantener el valor manual existente
      
      // Recalcular ITBIS Retenido
      detalle.itbis_retenido = calcularITBISRetenido(
        detalle.itbis_facturado,
        detalle.itbis_retenido_porcentaje
      );
      
      // Recalcular ISR
      detalle.isr_retenido = calcularISRPorTipo(
        detalle.monto,
        detalle.descuento,
        detalle.tipo_retencion,
        detalle.tipo_factura
      );
      
      if (prev.destino_itbis === 'costo') {
        detalle.itbis_proporcionalidad = 0;
        detalle.itbis_costo = detalle.itbis_facturado;
      } else if (prev.destino_itbis === 'proporcionalidad') {
        if (detalle.tipo_factura === 'Servicios') {
          detalle.itbis_proporcionalidad = detalle.itbis_facturado;
          detalle.itbis_costo = 0;
        } else {
          detalle.itbis_proporcionalidad = 0;
          detalle.itbis_costo = 0;
        }
      } else {
        detalle.itbis_proporcionalidad = 0;
        detalle.itbis_costo = 0;
      }
      
      // Recalcular Total Facturado
      detalle.total_facturado = calcularTotalFacturado(detalle);
      
      return {
        ...prev,
        detalles: nuevosDetalles
      };
    });
    
    toast.success(
      <div>
        <strong>✓ Línea recalculada</strong>
        <div className="text-sm mt-1">Todos los valores automáticos han sido actualizados</div>
      </div>,
      { autoClose: 2500 }
    );
  };

  const actualizarLinea = (index, campo, valor) => {
    setFormData(prev => {
      const nuevosDetalles = [...prev.detalles];
      nuevosDetalles[index] = {
        ...nuevosDetalles[index],
        [campo]: valor
      };

      // Auto-detectar tipo NCF cuando cambia el NCF
      if (campo === 'ncf') {
        const tipoDetectado = detectarTipoNCF(valor);
        if (tipoDetectado) {
          nuevosDetalles[index].tipo_ncf = tipoDetectado;
        }
      }

      // Auto-cálculo de Monto Facturado cuando cambia monto o descuento
      if (campo === 'monto' || campo === 'descuento') {
        const monto = campo === 'monto' ? valor : nuevosDetalles[index].monto;
        const descuento = campo === 'descuento' ? valor : nuevosDetalles[index].descuento;
        nuevosDetalles[index].monto_facturado = parseFloat(
          ((parseFloat(monto) || 0) - (parseFloat(descuento) || 0)).toFixed(2)
        );
      }

      // Auto-cálculo de ITBIS Facturado cuando cambia monto, descuento o porcentaje
      if (campo === 'monto' || campo === 'descuento' || campo === 'itbis_porcentaje') {
        const monto = campo === 'monto' ? valor : nuevosDetalles[index].monto;
        const descuento = campo === 'descuento' ? valor : nuevosDetalles[index].descuento;
        const porcentajeITBIS = campo === 'itbis_porcentaje' ? valor : nuevosDetalles[index].itbis_porcentaje;

        if (porcentajeITBIS && porcentajeITBIS !== 'exento' && porcentajeITBIS !== 0) {
          const montoBase = (parseFloat(monto) || 0) - (parseFloat(descuento) || 0);
          nuevosDetalles[index].itbis_facturado = parseFloat(
            (montoBase * (porcentajeITBIS / 100)).toFixed(2)
          );
        } else if (porcentajeITBIS === 'exento' || porcentajeITBIS === 0) {
          nuevosDetalles[index].itbis_facturado = 0;
        }
      }

      // Auto-cálculo de ITBIS retenido cuando cambian sus dependencias
      if (campo === 'itbis_retenido_porcentaje' || campo === 'itbis_facturado' || campo === 'monto' || campo === 'descuento' || campo === 'itbis_porcentaje') {
        const itbisFacturado = campo === 'itbis_facturado' ? valor : nuevosDetalles[index].itbis_facturado;
        const porcentajeRetencion = campo === 'itbis_retenido_porcentaje' ? valor : nuevosDetalles[index].itbis_retenido_porcentaje;
        nuevosDetalles[index].itbis_retenido = calcularITBISRetenido(
          parseFloat(itbisFacturado) || 0,
          porcentajeRetencion
        );
      }

      // Auto-cálculo de ISR retenido cuando cambia monto, descuento, tipo_retencion o tipo de factura
      if (campo === 'monto' || campo === 'descuento' || campo === 'tipo_retencion' || campo === 'tipo_factura') {
        const monto = campo === 'monto' ? valor : nuevosDetalles[index].monto;
        const descuento = campo === 'descuento' ? valor : nuevosDetalles[index].descuento;
        const tipoRetencion = campo === 'tipo_retencion' ? valor : nuevosDetalles[index].tipo_retencion;
        const tipoFactura = campo === 'tipo_factura' ? valor : nuevosDetalles[index].tipo_factura;
        
        nuevosDetalles[index].isr_retenido = calcularISRPorTipo(
          parseFloat(monto) || 0,
          parseFloat(descuento) || 0,
          tipoRetencion,
          tipoFactura
        );
        
        // Si cambia a Bienes, resetear ISR a 0
        if (tipoFactura === 'Bienes') {
          nuevosDetalles[index].tipo_retencion = '';
          nuevosDetalles[index].isr_retenido = 0;
        }
      }

      // Auto-rellenar campos cuando cambia Concepto
      if (campo === 'concepto') {
        const datos = obtenerDatosDeConcepto(valor);
        if (datos) {
          // Auto-rellenar pero permitir edición manual
          nuevosDetalles[index].tipo_gasto_606 = datos.tipo_gasto_606;
          nuevosDetalles[index].tipo_factura = datos.tipo_factura;
          nuevosDetalles[index].itbis_retenido_porcentaje = datos.itbis_retenido_porcentaje;
          nuevosDetalles[index].tipo_retencion = datos.tipo_retencion;
          
          if (datos.tipo_factura === 'Bienes') {
            nuevosDetalles[index].tipo_retencion = '';
            nuevosDetalles[index].isr_retenido = 0;
          }
          
          // Recalcular ITBIS retenido si hay ITBIS facturado
          if (nuevosDetalles[index].itbis_facturado > 0) {
            nuevosDetalles[index].itbis_retenido = calcularITBISRetenido(
              nuevosDetalles[index].itbis_facturado,
              datos.itbis_retenido_porcentaje
            );
          }
        }
      }

      if (campo === 'itbis_facturado' || campo === 'tipo_factura' || campo === 'concepto' || campo === 'monto' || campo === 'descuento' || campo === 'itbis_porcentaje') {
        const tipoFactura = nuevosDetalles[index].tipo_factura;
        const itbisFacturado = nuevosDetalles[index].itbis_facturado;

        if (prev.destino_itbis === 'costo') {
          nuevosDetalles[index].itbis_proporcionalidad = 0;
          nuevosDetalles[index].itbis_costo = itbisFacturado;
        } else if (prev.destino_itbis === 'proporcionalidad') {
          if (tipoFactura === 'Servicios') {
            nuevosDetalles[index].itbis_proporcionalidad = itbisFacturado;
            nuevosDetalles[index].itbis_costo = 0;
          } else {
            nuevosDetalles[index].itbis_proporcionalidad = 0;
            nuevosDetalles[index].itbis_costo = 0;
          }
        } else {
          nuevosDetalles[index].itbis_proporcionalidad = 0;
          nuevosDetalles[index].itbis_costo = 0;
        }
      }

      // Auto-calcular Total Facturado cuando cambian sus componentes
      if (campo === 'monto' || campo === 'descuento' || campo === 'itbis_facturado' || 
          campo === 'itbis_porcentaje' || campo === 'impuesto_selectivo' || 
          campo === 'otros_impuestos' || campo === 'propina_legal' || campo === 'otros_cargos') {
        nuevosDetalles[index].total_facturado = calcularTotalFacturado(nuevosDetalles[index]);
      }

      return {
        ...prev,
        detalles: nuevosDetalles
      };
    });
  };

  const handleProveedorSeleccionado = (proveedor, index) => {
    // Si index es null, es para el formulario
    if (index === null) {
      handleCambioFormularioDetalle('id_entidad_proveedor', proveedor.id_entidad);
      handleCambioFormularioDetalle('nombre_proveedor', `${proveedor.nombre} ${proveedor.apellido || ''}`.trim());
      handleCambioFormularioDetalle('rnc_proveedor', proveedor.rnc || proveedor.cedula || '');
    } else {
      actualizarLinea(index, 'id_entidad_proveedor', proveedor.id_entidad);
      actualizarLinea(index, 'nombre_proveedor', `${proveedor.nombre} ${proveedor.apellido || ''}`.trim());
      actualizarLinea(index, 'rnc_proveedor', proveedor.rnc || proveedor.cedula || '');
    }
    setMostrarModalProveedor(false);
    setLineaEditandoProveedor(null);
    toast.success(
      <div>
        <strong>✓ Proveedor seleccionado</strong>
        <div className="text-sm mt-1">{proveedor.nombre || 'Proveedor agregado'}</div>
      </div>,
      { autoClose: 2000 }
    );
  };

  const abrirModalProveedor = (index) => {
    setLineaEditandoProveedor(index);
    setMostrarModalProveedor(true);
  };

  const limpiarProveedor = (index) => {
    // Si index es null, es para el formulario
    if (index === null) {
      handleCambioFormularioDetalle('id_entidad_proveedor', null);
      handleCambioFormularioDetalle('nombre_proveedor', '');
      handleCambioFormularioDetalle('rnc_proveedor', '');
    } else {
      actualizarLinea(index, 'id_entidad_proveedor', null);
      actualizarLinea(index, 'nombre_proveedor', '');
      actualizarLinea(index, 'rnc_proveedor', '');
    }
  };

  // ==================== Keyboard Navigation ====================

  const handleKeyDown = (e, index, campo) => {
    // Enter en la última fila: agregar nueva línea
    if (e.key === 'Enter' && index === formData.detalles.length - 1) {
      e.preventDefault();
      agregarLinea();
    }

    // Escape: limpiar línea actual
    if (e.key === 'Escape') {
      e.preventDefault();
      const confirmar = window.confirm('¿Desea limpiar esta línea?');
      if (confirmar) {
        const lineaVacia = crearLineaVacia(formData.periodo);
        setFormData(prev => {
          const nuevosDetalles = [...prev.detalles];
          nuevosDetalles[index] = lineaVacia;
          return { ...prev, detalles: nuevosDetalles };
        });
      }
    }

    // Ctrl+Enter: guardar
    if (e.ctrlKey && e.key === 'Enter') {
      e.preventDefault();
      handleGuardar();
    }
  };

  // ==================== Autoguardado ====================

  /**
   * Compara si hay cambios significativos entre el estado actual y el anterior
   * Ignora campos que no afectan los datos (UI state)
   */
  const hayCambiosPendientes = useCallback(() => {
    if (!formDataAnteriorRef.current) return false;

    const anterior = formDataAnteriorRef.current;
    const actual = formData;

    // Comparar campos principales
    if (anterior.año !== actual.año) return true;
    if (anterior.periodo !== actual.periodo) return true;
    if (anterior.id_entidad_cliente !== actual.id_entidad_cliente) return true;
    if (anterior.destino_itbis !== actual.destino_itbis) return true;

    // Comparar detalles (longitud y contenido)
    if (anterior.detalles.length !== actual.detalles.length) return true;

    // Comparación profunda de detalles usando JSON
    return JSON.stringify(anterior.detalles) !== JSON.stringify(actual.detalles);
  }, [formData]);

  /**
   * Versión de guardarReporte específica para autoguardado
   * Acepta formData externo y no muestra toasts
   */
  const guardarReporteAutoguardado = useCallback(async (formDataExterno) => {
    try {
      const detallesParaAPI = formDataExterno.detalles.map(d => ({
        ...d,
        monto: parseFloat(d.monto) || 0,
        descuento: parseFloat(d.descuento) || 0,
        monto_facturado: parseFloat(d.monto_facturado) || 0,
        itbis_facturado: parseFloat(d.itbis_facturado) || 0,
        itbis_retenido: parseFloat(d.itbis_retenido) || 0,
        itbis_proporcionalidad: parseFloat(d.itbis_proporcionalidad) || 0,
        itbis_costo: parseFloat(d.itbis_costo) || 0,
        isr_retenido: parseFloat(d.isr_retenido) || 0,
        impuesto_selectivo: parseFloat(d.impuesto_selectivo) || 0,
        otros_impuestos: parseFloat(d.otros_impuestos) || 0,
        propina_legal: parseFloat(d.propina_legal) || 0,
        otros_cargos: parseFloat(d.otros_cargos) || 0,
        total_facturado: parseFloat(d.total_facturado) || 0,
      }));

      const payload = {
        año: formDataExterno.año,
        periodo: formDataExterno.periodo,
        id_entidad_cliente: formDataExterno.id_entidad_cliente,
        nombre_cliente: formDataExterno.nombre_cliente,
        rnc_cliente: formDataExterno.rnc_cliente,
        destino_itbis: formDataExterno.destino_itbis,
        detalles: detallesParaAPI
      };

      let response;
      const esNuevo = idReporteActual === null;

      if (esNuevo) {
        response = await instanciaAxios.post('/reporte-costo-gasto', payload);
      } else {
        response = await instanciaAxios.put(`/reporte-costo-gasto/${idReporteActual}`, payload);
      }

      const data = response.data;

      if (esNuevo) {
        setIdReporteActual(data.id_m_reporte_costo_gasto);
      }

      setModoEdicionReporte(true);
      return true;

    } catch (error) {
      if (esErrorPermiso(error)) return false;
      console.error('Error en autoguardado:', error);
      return false;
    }
  }, [idReporteActual]);

  /**
   * Ejecuta el autoguardado si hay cambios pendientes y hay líneas válidas
   * Solo guarda las líneas que pasan validación individual
   */
  const ejecutarAutoguardado = useCallback(async () => {
    // No autoguardar si no hay cliente (validación mínima requerida)
    if (!formData.id_entidad_cliente) {
      setEstadoAutoguardado('sin_cambios');
      return;
    }

    // No autoguardar si no hay destino ITBIS
    if (!formData.destino_itbis) {
      setEstadoAutoguardado('sin_cambios');
      return;
    }

    // Filtrar solo las líneas válidas usando validarLinea
    const lineasValidas = formData.detalles.filter(linea => {
      const validacion = validarLinea(linea);
      return validacion.isValid;
    });

    // No autoguardar si no hay líneas válidas
    if (lineasValidas.length === 0) {
      setEstadoAutoguardado('sin_cambios');
      return;
    }

    // Validar que no haya duplicados (NCF + RNC) antes de autoguardar
    const validacionDuplicados = validarNCFDuplicados(lineasValidas);
    if (!validacionDuplicados.isValid) {
      // No autoguardar si hay duplicados
      setEstadoAutoguardado('error');
      console.warn('Auto-save skipped: duplicate NCF+RNC detected', validacionDuplicados.errores);
      
      // Mostrar toast informativo (solo una vez, no en cada intento)
      if (estadoAutoguardado !== 'error') {
        toast.warning(
          <div>
            <strong>⚠ Autoguardado pausado</strong>
            <div className="text-sm mt-1">
              Se detectaron NCF duplicados. Corrija los duplicados para reanudar el autoguardado.
            </div>
          </div>,
          { autoClose: 5000 }
        );
      }
      return;
    }

    // Verificar si hay cambios reales comparando con el estado anterior
    if (!hayCambiosPendientes()) {
      setEstadoAutoguardado('sin_cambios');
      return;
    }

    setEstadoAutoguardado('guardando');

    try {
      // Crear payload solo con líneas válidas para el autoguardado
      const formDataParaGuardar = {
        ...formData,
        detalles: lineasValidas
      };

      const exito = await guardarReporteAutoguardado(formDataParaGuardar);

      if (exito) {
        // Actualizar referencia con el estado guardado (solo líneas válidas)
        formDataAnteriorRef.current = JSON.parse(JSON.stringify(formDataParaGuardar));
        setUltimoGuardado(new Date());
        setInfoAutoguardado({
          lineasGuardadas: lineasValidas.length,
          lineasTotales: formData.detalles.length
        });
        setEstadoAutoguardado('guardado');

        // Después de 5 segundos, volver a inactivo (más tiempo para leer el mensaje)
        setTimeout(() => setEstadoAutoguardado('inactivo'), 5000);
      } else {
        setEstadoAutoguardado('error');
      }
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error en autoguardado:', error);
      setEstadoAutoguardado('error');
    }
  }, [formData, hayCambiosPendientes, guardarReporteAutoguardado]);

  // Mantener referencia actualizada de la función (sin recrear el intervalo)
  useEffect(() => {
    ejecutarAutoguardadoRef.current = ejecutarAutoguardado;
  }, [ejecutarAutoguardado]);

  // Autoguardado cada 2 minutos (siempre activo) - Solo se ejecuta una vez al montar
  useEffect(() => {
    // Configurar intervalo de 2 minutos (120000 ms)
    intervaloAutoguardadoRef.current = setInterval(() => {
      // Usar la referencia para obtener siempre la versión más reciente de la función
      if (ejecutarAutoguardadoRef.current) {
        ejecutarAutoguardadoRef.current();
      }
      setSegundosRestantes(120); // Resetear countdown después de ejecutar
    }, 120000); // 2 minutos

    // Cleanup al desmontar
    return () => {
      if (intervaloAutoguardadoRef.current) {
        clearInterval(intervaloAutoguardadoRef.current);
      }
    };
  }, []); // Array vacío = solo se ejecuta al montar

  // Countdown visual para el autoguardado (actualiza cada segundo)
  useEffect(() => {
    const intervaloCountdown = setInterval(() => {
      setSegundosRestantes(prev => {
        if (prev <= 1) return 120; // Resetear a 2 minutos
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervaloCountdown);
  }, []);

  // Resetear referencia cuando se carga un nuevo reporte o se crea uno nuevo
  useEffect(() => {
    if (idReporteActual !== null || formData.id_entidad_cliente) {
      formDataAnteriorRef.current = JSON.parse(JSON.stringify(formData));
    }
  }, [idReporteActual, formData.id_entidad_cliente]);

  // ==================== Guardar/Cargar Reportes ====================

  /**
   * Función reutilizable para guardar el reporte (crear o actualizar)
   * @param {boolean} showToast - Si debe mostrar notificaciones de éxito/error
   * @returns {boolean} - true si el guardado fue exitoso, false en caso contrario
   */
  const guardarReporte = async (showToast = true) => {
    if (cargando) return false;
    // Validar que el cliente esté seleccionado
    if (!formData.id_entidad_cliente || !formData.nombre_cliente) {
      if (showToast) {
        toast.error(
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">👤</span>
              <strong className="text-base">Cliente Requerido</strong>
            </div>
            <div className="text-sm">
              Debe seleccionar un cliente en el encabezado antes de guardar
            </div>
          </div>,
          { autoClose: 5000 }
        );
      }
      return false;
    }

    // Validar que el destino ITBIS esté seleccionado
    if (!formData.destino_itbis) {
      if (showToast) {
        toast.error(
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">📊</span>
              <strong className="text-base">Destino ITBIS Requerido</strong>
            </div>
            <div className="text-sm">
              Debe seleccionar el destino del ITBIS (Proporcionalidad o Costo) en el encabezado antes de guardar
            </div>
          </div>,
          { autoClose: 5000 }
        );
      }
      return false;
    }

    if (formData.detalles.length === 0) {
      if (showToast) {
        toast.warning(
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">📝</span>
              <strong className="text-base">Sin Líneas para Guardar</strong>
            </div>
            <div className="text-sm">
              Agregue al menos una línea de detalle antes de guardar el reporte
            </div>
          </div>,
          { autoClose: 5000 }
        );
      }
      return false;
    }

    // Validar todas las líneas antes de guardar
    const validacion = validarTodas(formData.detalles);
    
    if (!validacion.isValid) {
      if (validacion.mensaje && showToast) {
        toast.error(validacion.mensaje, { autoClose: 8000 });
      }
      return false;
    }

    setCargando(true);

    try {
      // Convertir strings vacíos a 0 para campos numéricos antes de enviar al API
      const detallesParaAPI = formData.detalles.map(d => ({
        ...d,
        monto: parseFloat(d.monto) || 0,
        descuento: parseFloat(d.descuento) || 0,
        monto_facturado: parseFloat(d.monto_facturado) || 0,
        itbis_facturado: parseFloat(d.itbis_facturado) || 0,
        itbis_retenido: parseFloat(d.itbis_retenido) || 0,
        itbis_proporcionalidad: parseFloat(d.itbis_proporcionalidad) || 0,
        itbis_costo: parseFloat(d.itbis_costo) || 0,
        isr_retenido: parseFloat(d.isr_retenido) || 0,
        impuesto_selectivo: parseFloat(d.impuesto_selectivo) || 0,
        otros_impuestos: parseFloat(d.otros_impuestos) || 0,
        propina_legal: parseFloat(d.propina_legal) || 0,
        otros_cargos: parseFloat(d.otros_cargos) || 0,
        total_facturado: parseFloat(d.total_facturado) || 0,
      }));

      const payload = {
        año: formData.año,
        periodo: formData.periodo,
        id_entidad_cliente: formData.id_entidad_cliente,
        nombre_cliente: formData.nombre_cliente,
        rnc_cliente: formData.rnc_cliente,
        destino_itbis: formData.destino_itbis,
        detalles: detallesParaAPI
      };

      let response;
      const esNuevo = idReporteActual === null;
      
      if (esNuevo) {
        // Crear nuevo reporte
        response = await instanciaAxios.post('/reporte-costo-gasto', payload);
      } else {
        // Actualizar reporte existente
        response = await instanciaAxios.put(`/reporte-costo-gasto/${idReporteActual}`, payload);
      }

      const data = response.data;

      // Si es nuevo, establecer el ID del reporte
      if (esNuevo) {
        setIdReporteActual(data.id_m_reporte_costo_gasto);
      }

      setModoEdicionReporte(true);

      if (showToast) {
        toast.success(
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">✓</span>
              <strong className="text-base">Reporte Guardado</strong>
            </div>
            <div className="text-sm">
              {esNuevo 
                ? `Reporte #${data.id_m_reporte_costo_gasto} creado exitosamente` 
                : `Reporte #${idReporteActual} actualizado exitosamente`}
            </div>
          </div>,
          { autoClose: 4000 }
        );
      }

      return true;

    } catch (error) {
      if (esErrorPermiso(error)) return false;
      console.error('Error al guardar reporte:', error);
      if (showToast) {
        toast.error(
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">❌</span>
              <strong className="text-base">Error al Guardar</strong>
            </div>
            <div className="text-sm">
              {error.response?.data?.detalles || error.message || 'No se pudo guardar el reporte'}
            </div>
          </div>,
          { autoClose: 6000 }
        );
      }
      return false;
    } finally {
      setCargando(false);
    }
  };

  const handleGuardar = async () => {
    await guardarReporte(true);
  };

  const handleNuevoReporte = () => {
    const confirmar = window.confirm('¿Desea crear un nuevo reporte? Se perderán los datos no guardados.');
    if (confirmar) {
      setFormData({
        año: currentYear,
        periodo: currentMonth,
        id_entidad_cliente: null,
        nombre_cliente: '',
        rnc_cliente: '',
        destino_itbis: '',
        detalles: []
      });
      setIdReporteActual(null);
      setModoEdicionReporte(false);
      setReporteArchivado(false);
      setFormularioDetalle(crearLineaVacia(currentMonth));
      toast.info('Nuevo reporte iniciado', { autoClose: 2000 });
    }
  };

  const handleAbrirModalCargarReporte = async () => {
    setFiltrosReporte({
      año: currentYear,
      periodo: '',
      id_entidad_cliente: null,
      nombre_cliente_filtro: '',
      mostrarArchivados: false
    });
    setMostrarModalCargarReporte(true);
    await buscarReportes({ año: currentYear, periodo: '', id_entidad_cliente: null, mostrarArchivados: false });
  };

  const buscarReportes = async (filtrosCustom = null) => {
    setCargando(true);
    try {
      const filtros = filtrosCustom || filtrosReporte;
      
      const params = new URLSearchParams();
      if (filtros.año) params.append('año', filtros.año);
      if (filtros.periodo) params.append('periodo', filtros.periodo);
      if (filtros.id_entidad_cliente) params.append('id_entidad_cliente', filtros.id_entidad_cliente);
      if (filtros.mostrarArchivados) params.append('archivado', 'todos');
      
      const queryString = params.toString();
      const url = queryString ? `/reporte-costo-gasto?${queryString}` : '/reporte-costo-gasto';
      
      const response = await instanciaAxios.get(url);
      setReportesDisponibles(response.data);

    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar reportes:', error);
      toast.error(
        <div>
          <strong>Error al cargar reportes</strong>
          <div className="text-sm mt-1">{error.message}</div>
        </div>,
        { autoClose: 4000 }
      );
    } finally {
      setCargando(false);
    }
  };

  const handleCambioFiltroReporte = (campo, valor) => {
    const nuevosFiltros = { ...filtrosReporte, [campo]: valor };
    setFiltrosReporte(nuevosFiltros);
  };

  const handleClienteSeleccionadoFiltro = (cliente) => {
    const nuevosFiltros = {
      ...filtrosReporte,
      id_entidad_cliente: cliente.id_entidad,
      nombre_cliente_filtro: `${cliente.nombre} ${cliente.apellido || ''}`.trim()
    };
    setFiltrosReporte(nuevosFiltros);
    setMostrarModalCliente(false);
    // Buscar inmediatamente con el nuevo filtro
    buscarReportes(nuevosFiltros);
  };

  const limpiarFiltrosReporte = () => {
    const filtrosLimpios = {
      año: currentYear,
      periodo: '',
      id_entidad_cliente: null,
      nombre_cliente_filtro: '',
      mostrarArchivados: false
    };
    setFiltrosReporte(filtrosLimpios);
    buscarReportes(filtrosLimpios);
  };

  const handleArchivarReporte = async (idReporte, archivar, e) => {
    if (e) e.stopPropagation();
    try {
      await instanciaAxios.patch(`/reporte-costo-gasto/${idReporte}/archivar`, { archivado: archivar });
      toast.success(archivar ? 'Reporte archivado' : 'Reporte desarchivado', { autoClose: 2000 });
      buscarReportes();
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error('Error al archivar/desarchivar reporte', { autoClose: 3000 });
    }
  };

  const handleCargarReporte = async (idReporte) => {
    setCargando(true);
    try {
      const response = await instanciaAxios.get(`/reporte-costo-gasto/${idReporte}`);

      const data = response.data;

      // Convertir campos numéricos de string a number (pg devuelve NUMERIC como string)
      const detallesConvertidos = (data.detalles || []).map(d => ({
        ...d,
        monto: parseFloat(d.monto) || 0,
        descuento: parseFloat(d.descuento) || 0,
        monto_facturado: parseFloat(d.monto_facturado) || 0,
        itbis_facturado: parseFloat(d.itbis_facturado) || 0,
        itbis_retenido: parseFloat(d.itbis_retenido) || 0,
        itbis_proporcionalidad: parseFloat(d.itbis_proporcionalidad) || 0,
        itbis_costo: parseFloat(d.itbis_costo) || 0,
        isr_retenido: parseFloat(d.isr_retenido) || 0,
        impuesto_selectivo: parseFloat(d.impuesto_selectivo) || 0,
        otros_impuestos: parseFloat(d.otros_impuestos) || 0,
        propina_legal: parseFloat(d.propina_legal) || 0,
        otros_cargos: parseFloat(d.otros_cargos) || 0,
        total_facturado: parseFloat(d.total_facturado) || 0
      }));

      // Cargar datos en el formulario
      setFormData({
        año: data.año,
        periodo: data.periodo,
        id_entidad_cliente: data.id_entidad_cliente,
        nombre_cliente: data.nombre_cliente,
        rnc_cliente: data.rnc_cliente,
        destino_itbis: data.destino_itbis,
        detalles: detallesConvertidos
      });

      setIdReporteActual(data.id_m_reporte_costo_gasto);
      setModoEdicionReporte(true);
      setReporteArchivado(data.archivado || false);
      setMostrarModalCargarReporte(false);

      toast.success(
        <div>
          <strong>Reporte Cargado</strong>
          <div className="text-sm mt-1">
            Reporte #{data.id_m_reporte_costo_gasto} - {data.periodo} {data.año}
          </div>
        </div>,
        { autoClose: 3000 }
      );

    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar reporte:', error);
      toast.error(
        <div>
          <strong>Error al cargar reporte</strong>
          <div className="text-sm mt-1">{error.message}</div>
        </div>,
        { autoClose: 4000 }
      );
    } finally {
      setCargando(false);
    }
  };

  // ==================== CSV Export ====================

  const handleExportar = async () => {
    // Validar que el cliente esté seleccionado
    if (!formData.id_entidad_cliente || !formData.nombre_cliente) {
      toast.error(
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">👤</span>
            <strong className="text-base">Cliente Requerido</strong>
          </div>
          <div className="text-sm">
            Debe seleccionar un cliente en el encabezado antes de exportar el CSV
          </div>
        </div>,
        { autoClose: 5000 }
      );
      return;
    }

    // Validar que el destino ITBIS esté seleccionado
    if (!formData.destino_itbis) {
      toast.error(
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">📊</span>
            <strong className="text-base">Destino ITBIS Requerido</strong>
          </div>
          <div className="text-sm">
            Debe seleccionar el destino del ITBIS (Proporcionalidad o Costo) en el encabezado antes de exportar
          </div>
        </div>,
        { autoClose: 5000 }
      );
      return;
    }

    if (formData.detalles.length === 0) {
      toast.warning(
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">📝</span>
            <strong className="text-base">Sin Líneas para Exportar</strong>
          </div>
          <div className="text-sm">
            Agregue al menos una línea de detalle utilizando el formulario antes de exportar
          </div>
        </div>,
        { autoClose: 5000 }
      );
      return;
    }
    
    // Validar todas las líneas antes de exportar
    const validacion = validarTodas(formData.detalles);
    
    if (!validacion.isValid) {
      if (validacion.mensaje) {
        toast.error(
          <div>
            <strong>✗ Error de validación</strong>
            <div className="text-sm mt-1">{validacion.mensaje}</div>
          </div>,
          { autoClose: 5000 }
        );
        return;
      }
      
      // Mostrar errores detallados por línea
      toast.error(
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-red-300">
            <span className="text-2xl">❌</span>
            <div>
              <strong className="text-lg block">Errores de Validación</strong>
              <span className="text-xs text-red-600">Se encontraron {validacion.erroresPorLinea.length} línea(s) con errores</span>
            </div>
          </div>
          <div className="text-sm mt-3 space-y-3 max-h-96 overflow-y-auto">
            {validacion.erroresPorLinea.map(errorLinea => (
              <div key={errorLinea.linea} className="bg-red-50 rounded-lg p-3 border-l-4 border-red-500 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-red-600 text-white px-2 py-1 rounded font-bold text-xs">
                    LÍNEA {errorLinea.linea}
                  </span>
                  <span className="text-xs text-gray-600">
                    {errorLinea.errores.length} error(es)
                  </span>
                </div>
                <div className="space-y-1.5 ml-1">
                  {errorLinea.errores.map((error, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-red-700">
                      <span className="text-red-500 font-bold mt-0.5">•</span>
                      <span className="flex-1">{error}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-red-200 flex items-center justify-between">
            <div className="text-xs text-gray-600">
              💡 <strong>Consejo:</strong> Revise cada línea marcada y corrija los errores indicados
            </div>
          </div>
        </div>,
        {
          autoClose: false,
          closeButton: true,
          style: { maxWidth: '700px' }
        }
      );
      return;
    }
    
    // Validar NCF duplicados (BLOQUEANTE)
    const validacionDuplicados = validarNCFDuplicados(formData.detalles);
    
    if (!validacionDuplicados.isValid) {
      toast.error(
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-red-300">
            <span className="text-2xl">⚠️</span>
            <div>
              <strong className="text-lg block">NCF Duplicados Detectados</strong>
              <span className="text-xs text-red-600">{validacionDuplicados.errores.length} NCF(s) duplicado(s) encontrado(s)</span>
            </div>
          </div>
          <div className="text-sm mt-3 space-y-3">
            {validacionDuplicados.errores.map((error, idx) => (
              <div key={idx} className="bg-red-50 rounded-lg p-3 border-l-4 border-red-500 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-red-600 text-white px-2 py-1 rounded font-mono text-xs font-bold">
                    {error.ncf}
                  </span>
                </div>
                <div className="space-y-1 ml-1">
                  <div className="flex items-center gap-2 text-red-700">
                    <span className="font-semibold text-xs">Proveedor:</span>
                    <span>{error.proveedor}</span>
                  </div>
                  <div className="flex items-center gap-2 text-red-600">
                    <span className="font-semibold text-xs">RNC:</span>
                    <span>{error.rnc}</span>
                  </div>
                  <div className="flex items-center gap-2 text-red-700">
                    <span className="font-semibold text-xs">Aparece en líneas:</span>
                    <div className="flex gap-1">
                      {error.lineas.map((linea, i) => (
                        <span key={i} className="bg-red-600 text-white px-2 py-0.5 rounded text-xs font-bold">
                          {linea}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-red-200">
            <div className="text-xs text-gray-700 bg-yellow-50 p-2 rounded border border-yellow-300">
              <strong>⚠️ IMPORTANTE:</strong> No se puede exportar el CSV con NCF duplicados para el mismo proveedor. 
              Elimine o corrija las líneas duplicadas antes de continuar.
            </div>
          </div>
        </div>,
        {
          autoClose: false,
          closeButton: true,
          style: { maxWidth: '700px' }
        }
      );
      return;
    }
    
    // Guardar automáticamente antes de exportar
    const guardadoExitoso = await guardarReporte(false);
    
    if (!guardadoExitoso) {
      toast.error(
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">❌</span>
            <strong className="text-base">No se Pudo Guardar</strong>
          </div>
          <div className="text-sm">
            El reporte debe guardarse exitosamente antes de exportar. Por favor, revise los datos e intente nuevamente.
          </div>
        </div>,
        { autoClose: 6000 }
      );
      return;
    }
    
    try {
      exportarCSV(formData);
      toast.success(
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">✓</span>
            <strong className="text-base">Reporte Guardado y CSV Exportado</strong>
          </div>
          <div className="text-sm">
            <div className="font-semibold text-green-700">
              Reporte #{idReporteActual}
            </div>
            <div className="mt-1 text-gray-700">
              {formData.detalles.length} línea{formData.detalles.length !== 1 ? 's' : ''} • {formData.periodo} {formData.año}
            </div>
            <div className="mt-1 text-xs text-gray-600">
              ✓ Guardado en base de datos y CSV descargado
            </div>
          </div>
        </div>,
        { autoClose: 5000 }
      );
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al exportar CSV:', error);
      toast.error(
        <div className="max-w-md">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">✗</span>
            <strong className="text-base">Error al Exportar CSV</strong>
          </div>
          <div className="text-sm mt-2 bg-red-50 rounded-lg p-3 border border-red-200">
            <div className="font-semibold text-red-700 mb-1">
              Detalles del error:
            </div>
            <div className="text-red-600 text-xs">
              {error.message || 'Error desconocido al generar el archivo CSV'}
            </div>
            {error.stack && (
              <div className="text-red-500 text-xs mt-2 font-mono overflow-x-auto">
                {error.stack.split('\n')[0]}
              </div>
            )}
          </div>
          <div className="text-xs text-gray-600 mt-3 italic">
            Nota: El reporte fue guardado exitosamente en la base de datos.
          </div>
          <div className="text-xs text-gray-600 mt-1 italic">
            Por favor revise los datos e intente nuevamente. Si el problema persiste, contacte soporte.
          </div>
        </div>,
        {
          autoClose: 10000,
          style: { maxWidth: '550px' }
        }
      );
    }
  };

  const handleExportarTXT606 = async () => {
    if (!formData.id_entidad_cliente || !formData.nombre_cliente) {
      toast.error(
        <div>
          <strong>Cliente Requerido</strong>
          <div className="text-sm mt-1">Debe seleccionar un cliente antes de exportar el TXT 606</div>
        </div>,
        { autoClose: 5000 }
      );
      return;
    }

    if (!formData.destino_itbis) {
      toast.error(
        <div>
          <strong>Destino ITBIS Requerido</strong>
          <div className="text-sm mt-1">Debe seleccionar el destino del ITBIS antes de exportar</div>
        </div>,
        { autoClose: 5000 }
      );
      return;
    }

    if (formData.detalles.length === 0) {
      toast.warning('No hay líneas para exportar', { autoClose: 3000 });
      return;
    }

    const validacion = validarTodas(formData.detalles);
    if (!validacion.isValid) {
      if (validacion.mensaje) {
        toast.error(validacion.mensaje, { autoClose: 5000 });
        return;
      }
      toast.error(
        <div>
          <strong>Errores de Validación</strong>
          <div className="text-sm mt-1">
            {validacion.erroresPorLinea.length} línea(s) con errores. Corrija antes de exportar.
          </div>
        </div>,
        { autoClose: 5000 }
      );
      return;
    }

    const validacionDuplicados = validarNCFDuplicados(formData.detalles);
    if (!validacionDuplicados.isValid) {
      toast.error(
        <div>
          <strong>NCF Duplicados</strong>
          <div className="text-sm mt-1">
            {validacionDuplicados.errores.length} NCF(s) duplicado(s). Corrija antes de exportar.
          </div>
        </div>,
        { autoClose: 5000 }
      );
      return;
    }

    const guardadoExitoso = await guardarReporte(false);
    if (!guardadoExitoso) {
      toast.error('El reporte debe guardarse antes de exportar. Revise los datos.', { autoClose: 5000 });
      return;
    }

    try {
      exportarTXT606(formData);
      toast.success(
        <div>
          <strong>TXT DGII 606 Exportado</strong>
          <div className="text-sm mt-1">
            Reporte #{idReporteActual} - {formData.detalles.length} línea(s) - {formData.periodo} {formData.año}
          </div>
        </div>,
        { autoClose: 5000 }
      );
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al exportar TXT 606:', error);
      toast.error(
        <div>
          <strong>Error al Exportar TXT 606</strong>
          <div className="text-sm mt-1">{error.message || 'Error desconocido'}</div>
        </div>,
        { autoClose: 8000 }
      );
    }
  };

  const confirmarLimpiarFormulario = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = MESES[new Date().getMonth()];
    
    setFormData({
      año: currentYear,
      periodo: currentMonth,
      id_entidad_cliente: null,
      nombre_cliente: '',
      rnc_cliente: '',
      destino_itbis: '',
      detalles: []
    });
    
    setMostrarModalConfirmacionLimpiar(false);
    toast.success(
      <div>
        <strong>✓ Formulario limpiado</strong>
        <div className="text-sm mt-1">Todos los datos han sido eliminados exitosamente</div>
      </div>,
      { autoClose: 3000 }
    );
  };

  // ==================== Componentes auxiliares para celdas ====================

  // Celda de moneda con formato
  const CeldaMoneda = ({ valor, destacado = false }) => (
    <span className={destacado ? 'font-bold text-green-600' : 'text-gray-700'}>
      RD$ {(valor || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
    </span>
  );

  // Badge para Tipo NCF
  const BadgeTipoNCF = ({ tipo }) => {
    const colores = {
      'E34': 'bg-yellow-100 text-yellow-800',
      'B04': 'bg-yellow-100 text-yellow-800',
      'E31': 'bg-blue-100 text-blue-800',
      'B01': 'bg-green-100 text-green-800',
      'B02': 'bg-purple-100 text-purple-800',
      'E32': 'bg-purple-100 text-purple-800',
      'B11': 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colores[tipo] || 'bg-gray-100 text-gray-800'}`}>
        {tipo}
      </span>
    );
  };

  // Badge para Tipo Factura
  const BadgeTipoFactura = ({ tipo }) => {
    const colores = tipo === 'Bienes' ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-800';
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colores}`}>
        {tipo}
      </span>
    );
  };

  // Indicador de validez NCF
  const IndicadorNCF = ({ ncf }) => {
    if (!ncf) return null;
    const validacion = validarFormatoNCF(ncf);
    return validacion.isValid ? (
      <span className="text-green-500 mr-1 font-bold" title="NCF válido">✓</span>
    ) : (
      <span className="text-red-500 mr-1 font-bold" title={validacion.error}>✗</span>
    );
  };

  // ==================== Render ====================

  if (cargando) {
    return <SpinnerCargando />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Botón Toggle Sidebar - Flotante */}
        <button
          onClick={() => setSidebarOculto(!sidebarOculto)}
          className="fixed left-4 top-20 z-50 bg-color-primario text-white p-3 rounded-full shadow-lg hover:bg-color-secundario transition-all duration-200 hover:scale-110"
          title={sidebarOculto ? 'Mostrar menú lateral' : 'Ocultar menú lateral'}
        >
          {sidebarOculto ? <FaBars size={20} /> : <FaChevronLeft size={20} />}
        </button>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Costos y Gastos</h1>
            {modoEdicionReporte && idReporteActual && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  ✏️ Editando reporte para {formData.nombre_cliente || 'cliente'} - {formData.periodo} {formData.año}
                </span>
                {reporteArchivado && (
                  <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5">
                    <FaArchive size={12} />
                    Este reporte está archivado
                  </span>
                )}
              </div>
            )}
            {/* Indicador de Autoguardado */}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {/* Badge siempre visible de autoguardado activo */}
              <div
                className="group relative flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full text-xs font-medium border border-emerald-200 cursor-default"
                title={`Próximo autoguardado en ${Math.floor(segundosRestantes / 60)}:${(segundosRestantes % 60).toString().padStart(2, '0')}`}
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                Autoguardado activo
                {/* Tooltip con countdown */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                  <div className="text-center">
                    <div className="font-semibold">Próximo guardado en:</div>
                    <div className="text-lg font-mono mt-1">
                      {Math.floor(segundosRestantes / 60)}:{(segundosRestantes % 60).toString().padStart(2, '0')}
                    </div>
                  </div>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div className="border-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              </div>

              {/* Estados del autoguardado */}
              {estadoAutoguardado === 'guardando' && (
                <span className="flex items-center gap-1.5 text-blue-600 text-sm animate-pulse">
                  <FaSync className="animate-spin" size={12} />
                  Guardando...
                </span>
              )}
              {estadoAutoguardado === 'guardado' && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1.5 text-green-600 text-sm">
                    <span className="text-green-500">✓</span>
                    Guardado ({infoAutoguardado.lineasGuardadas} de {infoAutoguardado.lineasTotales} líneas)
                  </span>
                  {infoAutoguardado.lineasGuardadas < infoAutoguardado.lineasTotales && (
                    <span className="flex items-center gap-1 text-orange-500 text-xs bg-orange-50 px-2 py-0.5 rounded">
                      <span>⚠</span>
                      {infoAutoguardado.lineasTotales - infoAutoguardado.lineasGuardadas} con errores
                    </span>
                  )}
                </div>
              )}
              {estadoAutoguardado === 'error' && (
                <span className="flex items-center gap-1.5 text-orange-500 text-sm">
                  <span>⚠</span>
                  Error al autoguardar
                </span>
              )}
              {ultimoGuardado && estadoAutoguardado === 'inactivo' && (
                <span className="text-xs text-gray-400">
                  Último: {ultimoGuardado.toLocaleTimeString('es-DO')}
                </span>
              )}
            </div>
          </div>

          {/* Header Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Año */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Año
              </label>
              <input
                type="number"
                value={formData.año}
                onChange={(e) => handleCambioHeader('año', parseInt(e.target.value) || currentYear)}
                className="w-full p-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                min="2000"
                max="2100"
              />
            </div>

            {/* Periodo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Periodo
              </label>
              <select
                value={formData.periodo}
                onChange={(e) => handleCambioHeader('periodo', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
              >
                {MESES.map(mes => (
                  <option key={mes.value} value={mes.value}>
                    {mes.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Cliente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cliente <span className="text-red-500">*</span>
              </label>
              <InputClientes
                valor={formData.id_entidad_cliente}
                onClick={() => {
                  setContextoModalCliente('formulario');
                  setMostrarModalCliente(true);
                }}
                placeholder="Seleccionar cliente..."
                nombreCliente={formData.nombre_cliente}
                editable={true}
              />
            </div>

            {/* RNC Cliente (auto-filled) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                RNC Cliente
              </label>
              <input
                type="text"
                value={formData.rnc_cliente}
                readOnly
                className="w-full p-2 border border-gray-300 rounded-xl bg-gray-100 text-gray-600 cursor-not-allowed"
                placeholder="Auto-llenado"
              />
            </div>

            {/* Destino ITBIS */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destino ITBIS <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.destino_itbis}
                onChange={(e) => handleCambioDestinoITBIS(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                title="Seleccione dónde se colocará el ITBIS facturado (solo para servicios)"
              >
                <option value="">(Seleccione...)</option>
                <option value="proporcionalidad">ITBIS sujeto a Proporcionalidad (Art. 349)</option>
                <option value="costo">ITBIS llevado al Costo</option>
                <option value="adelantar">ITBIS por Adelantar</option>
              </select>
            </div>
          </div>
        </div>

        {/* Botón Agregar Múltiples Líneas */}
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => setMostrarModalBulkAdd(true)}
            disabled={!formData.destino_itbis}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
            title={!formData.destino_itbis ? 'Seleccione primero el Destino ITBIS en el encabezado' : 'Agregar múltiples líneas de forma eficiente'}
          >
            <FaLayerGroup size={16} />
            Agregar Múltiples Líneas
          </button>
        </div>

        {/* Formulario de Captura/Edición */}
        <FormularioDetalleGasto
          detalle={formularioDetalle}
          onChange={handleCambioFormularioDetalle}
          onGuardar={guardarDesdeFormulario}
          onCancelar={limpiarFormulario}
          onRecalcular={recalcularFormularioDetalle}
          onAbrirModalProveedor={() => abrirModalProveedor(null)}
          onLimpiarProveedor={() => limpiarProveedor(null)}
          modo={modoFormulario}
          lineaNumero={lineaEditandoIndex !== null ? lineaEditandoIndex + 1 : null}
        />

        {/* Resumen de líneas */}
        <div className="mb-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="text-lg font-semibold text-gray-700">
              📊 Líneas Capturadas: {formData.detalles.length}
            </div>
            {hayFiltrosActivos && (
              <button
                onClick={limpiarFiltrosLineas}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold hover:bg-blue-200 transition-colors flex items-center gap-1"
              >
                <FaTimes size={10} />
                Filtro activo ({lineasFiltradas.length}/{formData.detalles.length}) — Limpiar
              </button>
            )}
          </div>
          <div className="text-lg font-semibold text-green-600">
            Total Facturado: RD$ {totales.totalFacturado.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
          </div>
        </div>

        {/* Barra de Filtros Colapsable */}
        <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Header colapsable */}
          <button
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2 text-gray-700 font-medium">
              <FaSearch size={14} className="text-gray-500" />
              <span>Filtrar Líneas</span>
              {hayFiltrosActivos && (
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                  Mostrando {lineasFiltradas.length} de {formData.detalles.length}
                </span>
              )}
            </div>
            {mostrarFiltros ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
          </button>

          {/* Panel de filtros */}
          {mostrarFiltros && (
            <div className="p-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                {/* Filtro por NCF */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Buscar por NCF
                  </label>
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                    <input
                      type="text"
                      value={filtrosLineas.ncf}
                      onChange={(e) => setFiltrosLineas(prev => ({ ...prev, ncf: e.target.value }))}
                      placeholder="Ej: B01, E31..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Filtro por RNC */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Buscar por RNC del Proveedor
                  </label>
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                    <input
                      type="text"
                      value={filtrosLineas.rnc}
                      onChange={(e) => setFiltrosLineas(prev => ({ ...prev, rnc: e.target.value }))}
                      placeholder="Ej: 101234567..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Botón limpiar filtros */}
                <div>
                  <button
                    onClick={limpiarFiltrosLineas}
                    disabled={!hayFiltrosActivos}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <FaTimes size={12} />
                    Limpiar Filtros
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabla de Líneas (Read-only + Acciones) */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto max-w-full">
            <table className="min-w-full text-sm">
              <thead className="bg-color-secundario text-white">
                <tr>
                  <th className="sticky left-0 bg-color-secundario z-20 px-3 py-3 text-center">Acciones</th>
                  <th className="px-3 py-3 text-center">#</th>
                  <th className="px-3 py-3 text-center">Día</th>
                  <th className="px-3 py-3 text-left">NCF</th>
                  <th className="px-3 py-3 text-center">Tipo NCF</th>
                  <th className="px-3 py-3 text-left">Suplidor</th>
                  <th className="px-3 py-3 text-left">Concepto</th>
                  <th className="px-3 py-3 text-center">Tipo</th>
                  <th className="px-3 py-3 text-right">Monto</th>
                  <th className="px-3 py-3 text-right">Desc.</th>
                  <th className="px-3 py-3 text-right">ITBIS Fact.</th>
                  <th className="px-3 py-3 text-right">ITBIS Ret.</th>
                  <th className="px-3 py-3 text-right">ISR Ret.</th>
                  <th className="px-3 py-3 text-right">Total Fact.</th>
                  <th className="px-3 py-3 text-center">Forma Pago</th>
                </tr>
              </thead>
              <tbody>
                {formData.detalles.length === 0 ? (
                  <tr>
                    <td colSpan="15" className="text-center py-12 text-gray-500 italic">
                      No hay líneas capturadas. Complete el formulario arriba y haga clic en "Guardar Línea".
                    </td>
                  </tr>
                ) : lineasFiltradas.length === 0 ? (
                  <tr>
                    <td colSpan="15" className="text-center py-12 text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <FaSearch size={24} className="text-gray-400" />
                        <span className="italic">No se encontraron líneas con los filtros aplicados.</span>
                        <button
                          onClick={limpiarFiltrosLineas}
                          className="text-blue-600 hover:text-blue-800 underline text-sm"
                        >
                          Limpiar filtros
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  lineasFiltradas.map((detalle) => (
                    <tr
                      key={detalle._originalIndex}
                      className={`border-t border-gray-200 hover:bg-blue-50 transition-all duration-150 ${
                        modoFormulario === 'editando' && lineaEditandoIndex === detalle._originalIndex ? 'bg-blue-100 border-l-4 border-l-blue-500' : ''
                      }`}
                    >
                      {/* Acciones */}
                      <td className="sticky left-0 bg-white z-10 px-3 py-3 border-r border-gray-200">
                        <div className="flex gap-1 justify-center">
                          <button
                            onClick={() => editarLineaEnFormulario(detalle._originalIndex)}
                            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                            title="Editar línea"
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            onClick={() => duplicarLinea(detalle._originalIndex)}
                            className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                            title="Duplicar línea"
                          >
                            <FaCopy size={16} />
                          </button>
                          <button
                            onClick={() => eliminarLinea(detalle._originalIndex)}
                            className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                            title="Eliminar línea"
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                      </td>

                      {/* # */}
                      <td className="px-3 py-3 text-center text-gray-700 font-medium">
                        {detalle._originalIndex + 1}
                      </td>

                      {/* Día */}
                      <td className="px-3 py-3 text-center text-gray-700">
                        {detalle.dia}
                      </td>

                      {/* NCF */}
                      <td className="px-3 py-3 text-left">
                        <div className="flex items-center" title={detalle.ncf}>
                          <IndicadorNCF ncf={detalle.ncf} />
                          <span className="text-gray-700 text-sm">{detalle.ncf || '-'}</span>
                        </div>
                      </td>

                      {/* Tipo NCF */}
                      <td className="px-3 py-3 text-center">
                        {detalle.tipo_ncf ? <BadgeTipoNCF tipo={detalle.tipo_ncf} /> : '-'}
                      </td>

                      {/* Suplidor */}
                      <td className="px-3 py-3 text-left">
                        <div className="max-w-[200px] truncate" title={`${detalle.nombre_proveedor}\nRNC: ${detalle.rnc_proveedor}`}>
                          {detalle.nombre_proveedor || '-'}
                        </div>
                      </td>

                      {/* Concepto */}
                      <td className="px-3 py-3 text-left">
                        <div className="max-w-[180px] truncate" title={CONCEPTOS.find(c => c.value === detalle.concepto)?.label || detalle.concepto}>
                          {CONCEPTOS.find(c => c.value === detalle.concepto)?.label || detalle.concepto || '-'}
                        </div>
                      </td>

                      {/* Tipo Factura */}
                      <td className="px-3 py-3 text-center">
                        <BadgeTipoFactura tipo={detalle.tipo_factura} />
                      </td>

                      {/* Monto */}
                      <td className="px-3 py-3 text-right">
                        <CeldaMoneda valor={detalle.monto} />
                      </td>

                      {/* Descuento */}
                      <td className="px-3 py-3 text-right">
                        {detalle.descuento > 0 ? <CeldaMoneda valor={detalle.descuento} /> : '-'}
                      </td>

                      {/* ITBIS Facturado */}
                      <td className="px-3 py-3 text-right">
                        <CeldaMoneda valor={detalle.itbis_facturado} />
                      </td>

                      {/* ITBIS Retenido */}
                      <td className="px-3 py-3 text-right">
                        {detalle.itbis_retenido > 0 ? <CeldaMoneda valor={detalle.itbis_retenido} /> : '-'}
                      </td>

                      {/* ISR Retenido */}
                      <td className="px-3 py-3 text-right">
                        {detalle.isr_retenido > 0 ? <CeldaMoneda valor={detalle.isr_retenido} /> : '-'}
                      </td>

                      {/* Total Facturado */}
                      <td className="px-3 py-3 text-right">
                        <CeldaMoneda valor={detalle.total_facturado} destacado={true} />
                      </td>

                      {/* Forma Pago */}
                      <td className="px-3 py-3 text-center">
                        <span className="text-xs text-gray-600">
                          {detalle.forma_pago ? FORMAS_PAGO.find(f => f.value === detalle.forma_pago)?.label || detalle.forma_pago : '-'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totales Footer */}
        {formData.detalles.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 mt-4 animate-fadeIn">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Resumen de Totales</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">Subtotal (Monto)</div>
                <div className="text-lg font-semibold text-gray-800">
                  RD$ {totales.totalMonto.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">Descuentos</div>
                <div className="text-lg font-semibold text-orange-600">
                  -RD$ {totales.totalDescuento.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">ITBIS Facturado</div>
                <div className="text-lg font-semibold text-blue-600">
                  RD$ {totales.totalITBIS.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">ITBIS Retenido</div>
                <div className="text-lg font-semibold text-purple-600">
                  RD$ {totales.totalITBISRetenido.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-1">ISR Retenido</div>
                <div className="text-lg font-semibold text-indigo-600">
                  RD$ {totales.totalISRRetenido.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </div>
              </div>
              
              {totales.totalImpuestoSelectivo > 0 && (
                <div className="text-center">
                  <div className="text-xs text-gray-500 mb-1">Imp. Selectivo</div>
                  <div className="text-lg font-semibold text-gray-600">
                    RD$ {totales.totalImpuestoSelectivo.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              )}
              
              {totales.totalOtrosImpuestos > 0 && (
                <div className="text-center">
                  <div className="text-xs text-gray-500 mb-1">Otros Impuestos</div>
                  <div className="text-lg font-semibold text-gray-600">
                    RD$ {totales.totalOtrosImpuestos.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              )}
              
              {totales.totalPropina > 0 && (
                <div className="text-center">
                  <div className="text-xs text-gray-500 mb-1">Propinas</div>
                  <div className="text-lg font-semibold text-gray-600">
                    RD$ {totales.totalPropina.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              )}
              
              {totales.totalOtrosCargos > 0 && (
                <div className="text-center">
                  <div className="text-xs text-gray-500 mb-1">Otros Cargos</div>
                  <div className="text-lg font-semibold text-gray-600">
                    RD$ {totales.totalOtrosCargos.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-6 pt-4 border-t-2 border-gray-300">
              <div className="flex justify-end">
                <div className="text-center bg-color-secundario text-white px-8 py-3 rounded-xl shadow-md">
                  <div className="text-sm mb-1">Total General Facturado</div>
                  <div className="text-2xl font-bold">
                    RD$ {totales.totalFacturado.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Ayuda de Atajos y Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-6 shadow-sm">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">⌨️ Atajos de Teclado:</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-blue-700 mb-3">
            <div><kbd className="px-2 py-1 bg-white rounded border">Ctrl+Enter</kbd> Guardar línea (en formulario)</div>
            <div><kbd className="px-2 py-1 bg-white rounded border">Esc</kbd> Cancelar edición</div>
            <div><kbd className="px-2 py-1 bg-white rounded border">Tab</kbd> Navegar campos</div>
          </div>
          <div className="text-xs text-blue-600 mt-2 border-t border-blue-200 pt-2">
            💡 <strong>Tip:</strong> Use el formulario arriba para capturar/editar líneas. Haga clic en "Editar" para modificar una línea existente o "Duplicar" para copiarla.
          </div>
        </div>
      </div>

      {/* Modal Bulk Add Mejorado */}
      <ModalBulkAddLineasMejorado
        mostrar={mostrarModalBulkAdd}
        onClose={() => setMostrarModalBulkAdd(false)}
        onLineasAgregadas={handleLineasBulkAgregadas}
        periodoActual={formData.periodo}
        destinoITBIS={formData.destino_itbis}
      />

      {/* Modal Cliente */}
      <PlantillaModal
        mostrar={mostrarModalCliente}
        onClose={() => setMostrarModalCliente(false)}
        zIndex={60}
      >
        <TablaEntidad
          tipoEntidad={1}
          descripcion="Seleccione un cliente"
          setEntidadSeleccionada={handleClienteSeleccionado}
        />
      </PlantillaModal>

      {/* Modal Proveedor */}
      <PlantillaModal 
        mostrar={mostrarModalProveedor} 
        onClose={() => setMostrarModalProveedor(false)}
        zIndex={60}
      >
        <TablaEntidad
          tipoEntidad={2}
          descripcion="Seleccione un proveedor"
          setEntidadSeleccionada={(proveedor) => handleProveedorSeleccionado(proveedor, lineaEditandoProveedor)}
        />
      </PlantillaModal>

      {/* Modal Cargar Reporte */}
      <PlantillaModal 
        mostrar={mostrarModalCargarReporte} 
        onClose={() => setMostrarModalCargarReporte(false)}
      >
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Cargar Reporte Existente</h2>
          
          {/* Filtros */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Filtros de Búsqueda</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Año */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Año
                </label>
                <input
                  type="number"
                  value={filtrosReporte.año}
                  onChange={(e) => handleCambioFiltroReporte('año', parseInt(e.target.value) || '')}
                  className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="2000"
                  max="2100"
                  placeholder="Todos"
                />
              </div>

              {/* Periodo */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Periodo
                </label>
                <select
                  value={filtrosReporte.periodo}
                  onChange={(e) => handleCambioFiltroReporte('periodo', e.target.value)}
                  className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos</option>
                  {MESES.map(mes => (
                    <option key={mes.value} value={mes.value}>
                      {mes.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Cliente */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Cliente
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <InputClientes
                      valor={filtrosReporte.id_entidad_cliente}
                      onClick={() => {
                        setContextoModalCliente('filtro');
                        setMostrarModalCliente(true);
                      }}
                      placeholder="Todos los clientes"
                      nombreCliente={filtrosReporte.nombre_cliente_filtro || ''}
                      editable={true}
                    />
                  </div>
                  {filtrosReporte.id_entidad_cliente && (
                    <button
                      onClick={() => {
                        const nuevosFiltros = {
                          ...filtrosReporte,
                          id_entidad_cliente: null,
                          nombre_cliente_filtro: ''
                        };
                        setFiltrosReporte(nuevosFiltros);
                        buscarReportes(nuevosFiltros);
                      }}
                      className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                      title="Limpiar cliente"
                    >
                      <FaTimes size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Botones de filtro */}
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <button
                onClick={() => buscarReportes()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <FaSync size={14} />
                Buscar
              </button>
              <button
                onClick={limpiarFiltrosReporte}
                className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                <FaTimes size={14} />
                Limpiar
              </button>
              <label className="flex items-center gap-2 ml-2 cursor-pointer select-none">
                <div
                  className={`relative w-9 h-5 rounded-full transition-colors ${filtrosReporte.mostrarArchivados ? 'bg-amber-500' : 'bg-gray-300'}`}
                  onClick={() => {
                    const nuevosFiltros = { ...filtrosReporte, mostrarArchivados: !filtrosReporte.mostrarArchivados };
                    setFiltrosReporte(nuevosFiltros);
                    buscarReportes(nuevosFiltros);
                  }}
                >
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${filtrosReporte.mostrarArchivados ? 'translate-x-4' : ''}`} />
                </div>
                <span className="text-xs text-gray-600 flex items-center gap-1">
                  <FaArchive size={11} />
                  Mostrar archivados
                </span>
              </label>
              <span className="text-xs text-gray-500 ml-auto">
                {reportesDisponibles.length} reporte(s) encontrado(s)
              </span>
            </div>
          </div>

          {/* Lista de reportes */}
          {cargando ? (
            <div className="text-center py-8">
              <FaSync className="animate-spin text-blue-600 mx-auto mb-2" size={32} />
              <p className="text-gray-500">Cargando reportes...</p>
            </div>
          ) : reportesDisponibles.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-gray-500 mb-2">No se encontraron reportes</p>
              <p className="text-sm text-gray-400">Ajuste los filtros o cree un nuevo reporte</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              {reportesDisponibles.map((reporte) => (
                <div
                  key={reporte.id_m_reporte_costo_gasto}
                  className={`border rounded-lg p-3 transition-all cursor-pointer ${reporte.archivado ? 'border-amber-300 bg-amber-50/50 hover:border-amber-500' : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'}`}
                  onClick={() => handleCargarReporte(reporte.id_m_reporte_costo_gasto)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-800 text-sm">
                          #{reporte.id_m_reporte_costo_gasto}
                        </span>
                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap">
                          {reporte.periodo} {reporte.año}
                        </span>
                        {reporte.archivado && (
                          <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap flex items-center gap-1">
                            <FaArchive size={10} />
                            Archivado
                          </span>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-700 font-medium truncate">
                          {reporte.nombre_cliente}
                        </div>
                        <div className="text-xs text-gray-500">
                          RNC: {reporte.rnc_cliente || 'N/A'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-xs">
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded whitespace-nowrap">
                        {reporte.cantidad_lineas} líneas
                      </span>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded font-medium whitespace-nowrap">
                        RD$ {parseFloat(reporte.total_general || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      {reporte.archivado ? (
                        <button
                          onClick={(e) => handleArchivarReporte(reporte.id_m_reporte_costo_gasto, false, e)}
                          className="p-1.5 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
                          title="Desarchivar"
                        >
                          <FaBoxOpen size={14} />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => handleArchivarReporte(reporte.id_m_reporte_costo_gasto, true, e)}
                          className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Archivar"
                        >
                          <FaArchive size={14} />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCargarReporte(reporte.id_m_reporte_costo_gasto);
                        }}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium whitespace-nowrap"
                      >
                        Cargar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PlantillaModal>

      {/* Dock Vertical con acciones principales */}
      <DockVerticalDerecha
        handleBotonCrear={handleNuevoReporte}
        handleBotonBuscar={handleAbrirModalCargarReporte}
        handleBotonGuardar={formData.detalles.length > 0 ? handleGuardar : null}
        handleBotonPDF={formData.detalles.length > 0 ? handleExportar : null}
        handleBotonTXT={formData.detalles.length > 0 ? handleExportarTXT606 : null}
      />

      {/* Modal de Confirmación para Limpiar */}
      {mostrarModalConfirmacionLimpiar && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4 animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setMostrarModalConfirmacionLimpiar(false);
            }
          }}
        >
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl transform transition-all duration-300 scale-100">
            {/* Icono de advertencia */}
            <div className="flex justify-center mb-4">
              <div className="bg-red-100 rounded-full p-4">
                <FiTrash className="text-red-600" size={32} />
              </div>
            </div>

            {/* Título */}
            <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
              ¿Limpiar todo el formulario?
            </h3>

            {/* Mensaje */}
            <p className="text-gray-600 text-center mb-6">
              Esta acción eliminará todos los datos del formulario, incluyendo el cliente seleccionado y todas las líneas de detalle.
              <span className="block mt-2 font-semibold text-red-600">Esta acción no se puede deshacer.</span>
            </p>

            {/* Botones */}
            <div className="flex gap-3">
              <button
                onClick={() => setMostrarModalConfirmacionLimpiar(false)}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-all duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarLimpiarFormulario}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Sí, limpiar todo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
